import type { ChatTimelineMeta, TimelineTarget } from "shared/types";

/** Minimal sender shape shared by ChatResultItem and ThreadMessage senders. */
export interface TimelineSender {
  mapping_id: string;
  display_name: string;
}

/**
 * A renderable piece of a timeline line. `emphasis` segments are rendered in the
 * primary color (names/actors); plain segments are the connecting verb phrases.
 */
export interface TimelineSegment {
  text: string;
  emphasis?: boolean;
}

export function truncateName(name: string): string {
  return name;
}

/**
 * Join display names per the timeline contract:
 *   1 → "A"
 *   2 → "A and B"
 *   3 → "A, B and C"
 *   4+ → "A, B, C and N others"  (N = total - 3, "other" when N === 1)
 */
export function joinNames(names: string[]): string {
  const clean = names.filter(Boolean).map(truncateName);
  const n = clean.length;
  if (n === 0) return "";
  if (n === 1) return clean[0];
  if (n === 2) return `${clean[0]} and ${clean[1]}`;
  if (n === 3) return `${clean[0]}, ${clean[1]} and ${clean[2]}`;
  const others = n - 3;
  return `${clean[0]}, ${clean[1]}, ${clean[2]} and ${others} ${
    others === 1 ? "other" : "others"
  }`;
}

function targetNames(targets: TimelineTarget[] | undefined): string {
  return joinNames((targets ?? []).map((t) => t.display_name ?? ""));
}

function actorLabel(
  meta: ChatTimelineMeta,
  sender: TimelineSender,
  resourceId: string,
): string {
  const mappingId = meta.actor?.mapping_id ?? sender.mapping_id;
  if (mappingId && mappingId === resourceId) return "You";
  return meta.actor?.display_name || sender.display_name || "Someone";
}

/**
 * Suffix from `source` — applied only to the listed actions (member add/remove,
 * assignee_bulk_change, customer_swap). "human" → nothing.
 */
function sourceSuffix(source?: string): TimelineSegment[] {
  if (source === "automation") return [{ text: " via automation" }];
  if (source === "api") return [{ text: " via API" }];
  return [];
}

/**
 * True when the member row is the actor acting on themselves (join/left).
 * Self is only meaningful in active voice (actor_hidden === false).
 */
function isSelfTimeline(meta: ChatTimelineMeta | undefined): boolean {
  if (!meta || meta.actor_hidden) return false;
  const actorId = meta.actor?.mapping_id;
  if (!actorId) return false;
  const targets =
    meta.action === "member_removed"
      ? meta.targets_removed
      : meta.targets_added;
  return targets?.length === 1 && targets[0]?.mapping_id === actorId;
}

/**
 * assignee_bulk_change — always passive (no actor / no "You"), mirroring the
 * "was assigned to the ticket" line. add-only / remove-only / both.
 */
function assigneeChangeSegments(meta: ChatTimelineMeta): TimelineSegment[] {
  const added = meta.targets_added ?? [];
  const removed = meta.targets_removed ?? [];
  const suffix = sourceSuffix(meta.source);
  const addWasWere = added.length > 1 ? " were" : " was";
  const removeWasWere = removed.length > 1 ? " were" : " was";

  if (added.length && removed.length) {
    return [
      { text: targetNames(added), emphasis: true },
      { text: `${addWasWere} assigned and ` },
      { text: targetNames(removed), emphasis: true },
      { text: `${removeWasWere} unassigned from the ticket` },
      ...suffix,
    ];
  }
  if (added.length) {
    return [
      { text: targetNames(added), emphasis: true },
      { text: `${addWasWere} assigned to the ticket` },
      ...suffix,
    ];
  }
  if (removed.length) {
    return [
      { text: targetNames(removed), emphasis: true },
      { text: `${removeWasWere} unassigned from the ticket` },
      ...suffix,
    ];
  }
  return [];
}

/** member_added / member_removed — active / passive / self, array-driven. */
function memberChangeSegments(
  meta: ChatTimelineMeta,
  actor: string,
  added: boolean,
): TimelineSegment[] {
  const targets = added ? meta.targets_added : meta.targets_removed;
  const names = targetNames(targets);

  // Self join/left — no suffix (self implies a human actor acting on themselves).
  if (isSelfTimeline(meta)) {
    return [
      { text: actor, emphasis: true },
      { text: added ? " joined the conversation" : " left the conversation" },
    ];
  }

  const suffix = sourceSuffix(meta.source);

  // Passive voice for both active and hidden actors:
  //   "{name} was added to the conversation" / "{names} were removed ...".
  const count = targets?.length ?? 0;
  const wasWere = count > 1 ? "were" : "was";
  return [
    { text: names, emphasis: true },
    {
      text: added
        ? ` ${wasWere} added to the conversation`
        : ` ${wasWere} removed from the conversation`,
    },
    ...suffix,
  ];
}

/**
 * Build the client-side timeline sentence from `timeline_meta`.
 *
 * We deliberately do NOT render the server-provided `message` string — the line
 * is generated on the client per action, entirely from the arrays + `actor` +
 * `actor_hidden` + `source`. Legacy `target_*` scalars are ignored.
 */
export function buildTimelineSentence(
  meta: ChatTimelineMeta | undefined,
  sender: TimelineSender,
  resourceId: string,
): TimelineSegment[] {
  if (!meta) return [];

  const actor = actorLabel(meta, sender, resourceId);

  switch (meta.action) {
    case "assignee_bulk_change":
      return assigneeChangeSegments(meta);
    case "customer_swap":
      return [
        { text: "User was changed from " },
        {
          text: truncateName(meta.targets_removed?.[0]?.display_name ?? ""),
          emphasis: true,
        },
        { text: " to " },
        {
          text: truncateName(meta.targets_added?.[0]?.display_name ?? ""),
          emphasis: true,
        },
        ...sourceSuffix(meta.source),
      ];
    case "started_conversation":
      return [
        { text: actor, emphasis: true },
        { text: " started the conversation" },
      ];
    case "join_conversation":
      return [
        { text: actor, emphasis: true },
        { text: " joined the conversation" },
      ];
    case "member_added":
      return memberChangeSegments(meta, actor, true);
    case "member_removed":
      return memberChangeSegments(meta, actor, false);
    default:
      return [
        { text: actor, emphasis: true },
        { text: " updated the conversation" },
      ];
  }
}
